import React, { useState, useEffect } from 'react';
import { 
  X, ChevronLeft, ChevronRight, Sparkles, LayoutDashboard, 
  AlertTriangle, Lightbulb, Clock, Shield, CheckCircle, Cloud
} from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import axios from 'axios';
import CloudMultiSelect from './CloudMultiSelect';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OnboardingTour = ({ isOpen, onClose, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedProviders, setSelectedProviders] = useState([]);

  // Check if user is admin - admins skip provider selection
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = user.role === 'admin';

  const saveProviderPreferences = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/user/preferences`, {
        selected_providers: selectedProviders,
        onboarding_completed: true
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (e) {
      // Silently fail - localStorage will handle it
    }
    localStorage.setItem('selected_providers', JSON.stringify(selectedProviders));
    localStorage.setItem('onboarding_completed', 'true');
  };

  const steps = [
    // Step 0: Provider Selection (skip for admin)
    ...(!isAdmin ? [{
      title: "Which clouds do you use?",
      description: "Select all the cloud platforms you want to optimize. We'll customize your experience based on your selection.",
      icon: Cloud,
      color: "violet",
      highlight: null,
      position: "center",
      isProviderSelection: true
    }] : []),
    {
      title: "Welcome to CARTIEAI! 🎉",
      description: "Your AI-powered cloud cost intelligence platform. Let's take a quick tour to help you get the most out of it.",
      icon: Sparkles,
      color: "emerald",
      highlight: null,
      position: "center"
    },
    {
      title: "Dashboard Overview",
      description: "Get a bird's eye view of your cloud spending. See total costs, anomalies, savings opportunities, and budget status at a glance.",
      icon: LayoutDashboard,
      color: "blue",
      highlight: "[data-testid='nav-dashboard']",
      tip: "Pro tip: Click 'Refresh' to get the latest data"
    },
    {
      title: "AI Cost Advisor",
      description: "Your 24/7 FinOps expert. Ask questions like 'Why is my AWS bill high?' or 'How can I save money?' and get instant, actionable answers.",
      icon: Sparkles,
      color: "emerald",
      highlight: "[data-testid='nav-ai-advisor']",
      tip: "Pro tip: Try asking 'What are my top 3 savings opportunities?'"
    },
    {
      title: "Anomaly Detection",
      description: "Our ML algorithms automatically detect unusual cost patterns. Get alerted before small issues become big problems.",
      icon: AlertTriangle,
      color: "amber",
      highlight: null,
      tip: "Pro tip: Set up Slack notifications for real-time alerts"
    },
    {
      title: "Smart Recommendations",
      description: "AI-generated suggestions to reduce costs: idle resources, right-sizing opportunities, reserved instance purchases, and more.",
      icon: Lightbulb,
      color: "yellow",
      highlight: null,
      tip: "Pro tip: Implement recommendations with one click"
    },
    {
      title: "Resource Scheduler",
      description: "Automatically stop non-production resources outside business hours. Save up to 65% on dev/test environments.",
      icon: Clock,
      color: "purple",
      highlight: null,
      tip: "Pro tip: Start with dev environments, typically the biggest quick win"
    },
    {
      title: "Budget Guardrails",
      description: "Set spending limits and get alerts at 70%, 90%, and 100% thresholds. Never be surprised by your cloud bill again.",
      icon: Shield,
      color: "rose",
      highlight: null,
      tip: "Pro tip: Create guardrails for each team or project"
    },
    {
      title: "You're All Set! 🚀",
      description: "You're ready to start saving. Begin by asking the AI Advisor how to reduce your costs, or explore the dashboard to understand your current spending.",
      icon: CheckCircle,
      color: "emerald",
      highlight: null,
      position: "center",
      cta: "Start Saving Now"
    }
  ];

  const currentStepData = steps[currentStep];
  const isLastStep = currentStep === steps.length - 1;
  const isFirstStep = currentStep === 0;

  const getColorClasses = (color) => {
    // v34.99 (Feb 14 2026) — Replaced pastel `bg-{c}-100` / `dark:bg-{c}-900/30`
    // icon backgrounds (looked dull/washed-out in the "Resource Scheduler" step
    // and others — founder spotted them) with VIBRANT gradient + white icon +
    // colored glow pattern. Matches the WhatCartieCanDoNow + Careers heart-icon
    // aesthetic. Each step's icon now pops against the white modal.
    const colors = {
      emerald: { bg: 'bg-emerald-500 bg-gradient-to-br from-emerald-500 to-teal-500',     text: 'text-white', border: 'border-emerald-400/50', glow: 'shadow-emerald-500/40' },
      blue:    { bg: 'bg-sky-500 bg-gradient-to-br from-sky-500 to-blue-500',         text: 'text-white', border: 'border-sky-400/50',     glow: 'shadow-sky-500/40' },
      amber:   { bg: 'bg-amber-500 bg-gradient-to-br from-amber-500 to-orange-500',     text: 'text-white', border: 'border-amber-400/50',   glow: 'shadow-amber-500/40' },
      yellow:  { bg: 'bg-yellow-400 bg-gradient-to-br from-yellow-400 to-amber-500',     text: 'text-white', border: 'border-yellow-400/50',  glow: 'shadow-yellow-500/40' },
      purple:  { bg: 'bg-violet-500 bg-gradient-to-br from-violet-500 to-fuchsia-500',   text: 'text-white', border: 'border-violet-400/50',  glow: 'shadow-violet-500/40' },
      rose:    { bg: 'bg-rose-500 bg-gradient-to-br from-rose-500 to-pink-500',        text: 'text-white', border: 'border-rose-400/50',    glow: 'shadow-rose-500/40' },
      violet:  { bg: 'bg-violet-500 bg-gradient-to-br from-violet-500 to-indigo-500',    text: 'text-white', border: 'border-violet-400/50',  glow: 'shadow-violet-500/40' },
    };
    return colors[color] || colors.emerald;
  };

  const colorClasses = getColorClasses(currentStepData.color);

  const handleNext = async () => {
    // If on provider selection step, save preferences
    if (currentStepData.isProviderSelection) {
      if (selectedProviders.length === 0) {
        // Must select at least one provider
        return;
      }
      await saveProviderPreferences();
    }
    
    if (isLastStep) {
      onComplete?.();
      onClose();
      // Reload to apply provider filtering
      window.location.reload();
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (!isFirstStep) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSkip = () => {
    // If skipping without selecting providers, set all providers for admin or default for others
    const existingProviders = localStorage.getItem('selected_providers');
    if (!existingProviders || JSON.parse(existingProviders).length === 0) {
      // Default to all providers if none selected
      localStorage.setItem('selected_providers', JSON.stringify(['aws', 'azure', 'gcp', 'snowflake']));
    }
    onClose();
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') handleNext();
      if (e.key === 'ArrowLeft') handlePrev();
    };
    
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, currentStep]);

  if (!isOpen) return null;

  const IconComponent = currentStepData.icon;

  return (
    <div
      className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center p-0 sm:p-4 top-[64px]"
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0)' }}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={handleSkip} />

      {/* Modal — bottom sheet on mobile (full-width, rounded-top), centered card on >=sm */}
      <div
        className="relative w-full sm:max-w-md max-h-[92vh] sm:max-h-[85vh] bg-white dark:bg-slate-900 rounded-t-3xl sm:rounded-2xl shadow-2xl overflow-hidden flex flex-col"
        data-testid="onboarding-tour-modal"
      >
        {/* Drag-handle indicator (mobile) — purely cosmetic, signals "this is a sheet" */}
        <div className="flex sm:hidden justify-center pt-2 pb-1">
          <div className="w-10 h-1 rounded-full bg-slate-300 dark:bg-slate-700" />
        </div>

        {/* Close button — bigger on mobile (44px) for easy tap; smaller on >=sm */}
        <button
          onClick={handleSkip}
          className="absolute top-2 right-2 sm:top-3 sm:right-3 z-20 w-11 h-11 sm:w-8 sm:h-8 inline-flex items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800 text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors shadow"
          data-testid="onboarding-tour-close"
          aria-label="Close tour"
        >
          <X className="w-5 h-5 sm:w-4 sm:h-4" />
        </button>

        {/* Progress bar */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-slate-200 dark:bg-slate-700">
          <div
            className="h-full bg-emerald-500 transition-all duration-300"
            style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
          />
        </div>

        {/* Scrollable content area */}
        <div className="flex-1 overflow-y-auto px-5 pt-6 pb-4 sm:p-6 sm:pt-8">
          {/* Icon — v34.99 (Feb 14 2026): vibrant gradient + white icon + colored
              glow. Was a flat pastel (bg-{c}-100/dark:{c}-900) that looked
              washed-out in the "Resource Scheduler" step. */}
          <div className={`w-14 h-14 rounded-2xl ${colorClasses.bg} flex items-center justify-center mx-auto mb-4 shadow-lg ${colorClasses.glow}`}>
            <IconComponent className={`w-7 h-7 ${colorClasses.text}`} strokeWidth={2.4} />
          </div>

          {/* Title */}
          <h2 className="text-lg sm:text-xl font-bold text-center text-slate-900 dark:text-white mb-2">
            {currentStepData.title}
          </h2>

          {/* Description */}
          <p className="text-sm text-center text-slate-400 dark:text-slate-400 mb-4 leading-relaxed">
            {currentStepData.description}
          </p>

          {/* Provider Selection UI */}
          {currentStepData.isProviderSelection && (
            <div className="mb-2">
              <CloudMultiSelect
                value={selectedProviders}
                onChange={setSelectedProviders}
                mode="multi"
                placeholder="Click to pick your clouds…"
                dataTestId="onboarding-tour-cloud-picker"
              />
              <p className="text-[11px] text-slate-400 dark:text-slate-400 text-center mt-2">
                15 cloud providers supported. <span className="text-emerald-600 dark:text-emerald-400 font-semibold">6 LIVE</span> · 9 coming soon.
              </p>
            </div>
          )}

          {/* Selected count for provider selection */}
          {currentStepData.isProviderSelection && (
            <p className="text-center text-xs text-slate-400 mb-3">
              {selectedProviders.length === 0 
                ? 'Please select at least one provider'
                : `${selectedProviders.length} provider${selectedProviders.length > 1 ? 's' : ''} selected`
              }
            </p>
          )}

          {/* Tip */}
          {currentStepData.tip && (
            <div className="bg-slate-50 dark:bg-slate-800 rounded-lg px-3 py-2 mb-4">
              <p className="text-xs text-slate-400 dark:text-slate-400">
                💡 {currentStepData.tip}
              </p>
            </div>
          )}

          {/* Step indicators */}
          <div className="flex items-center justify-center gap-1.5 mb-4">
            {steps.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentStep(idx)}
                className={`w-2.5 h-2.5 sm:w-2 sm:h-2 rounded-full transition-all ${
                  idx === currentStep
                    ? 'w-7 sm:w-6 bg-emerald-500'
                    : idx < currentStep
                      ? 'bg-emerald-300'
                      : 'bg-slate-300 dark:bg-slate-600'
                }`}
                aria-label={`Go to step ${idx + 1}`}
              />
            ))}
          </div>
        </div>

        {/* Sticky footer navigation —
            Mobile (<sm): primary CTA full-width on top; Back & Skip below as smaller text.
            >=sm: classic 3-button row. */}
        <div
          className="border-t border-slate-200 dark:border-slate-800 bg-slate-50/80 dark:bg-slate-900 backdrop-blur-sm px-4 py-3 sm:px-6 sm:py-4"
          style={{ paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 12px)' }}
        >
          {/* Mobile layout — stacked */}
          <div className="flex sm:hidden flex-col gap-2">
            <Button
              onClick={handleNext}
              disabled={currentStepData.isProviderSelection && selectedProviders.length === 0}
              className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-white text-base font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="onboarding-tour-next-mobile"
            >
              {currentStepData.isProviderSelection
                ? 'Continue'
                : isLastStep
                  ? (currentStepData.cta || 'Finish')
                  : 'Next'
              }
              {!isLastStep && !currentStepData.isProviderSelection && <ChevronRight className="w-5 h-5 ml-1" />}
            </Button>
            <div className="flex items-center justify-between">
              <Button
                variant="ghost"
                onClick={handlePrev}
                disabled={isFirstStep}
                size="sm"
                className="text-slate-400 disabled:opacity-30 px-3 h-10"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back
              </Button>
              <Button
                variant="ghost"
                onClick={handleSkip}
                size="sm"
                className="text-slate-400 px-3 h-10"
                data-testid="onboarding-tour-skip"
              >
                Skip tour
              </Button>
            </div>
          </div>

          {/* Desktop layout — 3-button row */}
          <div className="hidden sm:flex items-center justify-between gap-2">
            <Button
              variant="ghost"
              onClick={handlePrev}
              disabled={isFirstStep}
              size="sm"
              className="text-slate-400"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Back
            </Button>

            <Button
              variant="ghost"
              onClick={handleSkip}
              size="sm"
              className="text-slate-400"
              data-testid="onboarding-tour-skip-desktop"
            >
              Skip tour
            </Button>

            <Button
              onClick={handleNext}
              disabled={currentStepData.isProviderSelection && selectedProviders.length === 0}
              size="sm"
              className="bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="onboarding-tour-next"
            >
              {currentStepData.isProviderSelection
                ? 'Continue'
                : isLastStep
                  ? (currentStepData.cta || 'Finish')
                  : 'Next'
              }
              {!isLastStep && !currentStepData.isProviderSelection && <ChevronRight className="w-4 h-4 ml-1" />}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingTour;
